<template>
    <div class="credit-card-form">
        <template v-if="mode === 'new'">
            <CreditCardNewForm
                :error="cardError"
                :on-mount="handleMount"
            />
        </template>

        <template v-else-if="mode === 'existing'">
            <CreditCardExistingForm
                :error="cardError"
                :is-editing="isEditing"
                :on-edit-change="handleEditChange"
                :on-mount="handleMount"
            />
        </template>

        <slot v-bind="slotProps" />
    </div>
</template>
<script>
/* global Stripe */

import { isEmpty } from 'lodash';
import { mapMutations, mapState } from 'vuex';
import apiCall from '@/utils/apiCall';
import style from '@/utils/form/stripeElements';
import {
    CreditCardNewForm,
    CreditCardExistingForm,
} from '@/components/Billing';
import { loadScript } from '@/utils/scriptLoader';

const MODE_NEW = 'new';
const MODE_EXISTING = 'existing';

export default {
    components: {
        CreditCardNewForm,
        CreditCardExistingForm,
    },

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        stripe: null,
        card: null,
        mode: null,
        isEditing: false,
    }),

    computed: {
        ...mapState(['user', 'userCard']),

        ...mapState('show/modal', [
            'cardError',
        ]),

        /**
         * Gets the slot props.
         *
         * @returns {object} Slot props object.
         */
        slotProps() {
            return {
                hasError: this.hasError,
                getPaymentMethod: this.getPaymentMethod,
                isNew: this.isNew,
            };
        },
    },

    watch: {
        /**
         * Handles change of "disabled" prop.
         *
         * @param  {boolean} disabled "disabled" state
         */
        disabled(disabled) {
            if (this.card) {
                this.card.update({ disabled });
            }
        },
    },

    /**
     * Mounted hook.
     */
    mounted() {
        this.mode = isEmpty(this.userCard) ? MODE_NEW : MODE_EXISTING;
    },

    methods: {
        ...mapMutations('show/modal', [
            'setCardError',
        ]),

        /**
         * Handles components mount.
         *
         * @param  {object} ref Credit card reference element.
         */
        handleMount(ref) {
            loadScript('js.stripe.com/v3/', 'stripe', () => {
                this.configureStripe(ref);
            });
        },

        /**
         * Configures stripe
         *
         * @param {HTMLElement} ref Card element.
         */
        configureStripe(ref) {
            this.stripe = Stripe(process.env.MIX_STRIPE_KEY);

            const elements = this.stripe.elements();

            this.card = elements.create('card', style);
            this.card.addEventListener('change', this.handleChange.bind(this));
            this.card.mount(ref);
        },

        /**
         * Handles card data change.
         *
         * @param {Event} event Change event.
         */
        handleChange(event) {
            this.setCardError(event.error ? event.error.message : null);
        },

        /**
         * Handles card edit status change.
         *
         * @param  {boolean} isEditing "is editing" state
         */
        handleEditChange(isEditing) {
            this.isEditing = isEditing;
        },

        /**
         * Checks whether there are any errors.
         *
         * @returns {boolean} Check result.
         */
        hasError() {
            return !! this.cardError;
        },

        /**
         * Gets the configured payment method.
         */
        async getPaymentMethod() {
            return this.mode === MODE_NEW
                ? await this.createPaymentMethod()
                : await this.fetchPaymentMethod();
        },

        /**
         * Creates new payment method.
         *
         * @returns {object} Payment method.
         */
        async createPaymentMethod() {
            const setup = await this.setupPaymentIntent();
            const { setupIntent: intent, error } = await this.stripe.confirmCardSetup(
                setup.client_secret,
                {
                    payment_method: {
                        card: this.card,
                        billing_details: this.user.name,
                    },
                }
            );

            if (error) {
                this.setCardError(error.message);

                throw new Error(error.message, { cause: 'credit_card' });
            }

            return intent.payment_method;
        },

        /**
         * Creates payment intent setup.
         */
        async setupPaymentIntent() {
            this.setCardError(null);

            const response = await apiCall(this, 'get', `${this.$appUrl}/api/billing/intent`);
            const { status, data } = response;

            if (400 <= status) {
                this.setCardError(data.message);

                throw new Error(data.message, { cause: 'credit_card' });
            }

            return data;
        },

        /**
         * Gets the stored user payment method.
         *
         * @returns {string} Payment method.
         */
        fetchPaymentMethod() {
            if (this.isEditing) {
                return this.createPaymentMethod();
            }

            return this.userCard.id;
        },

        /**
         * Indicates whether credit card is new.
         *
         * @returns {boolean} Is credit card new?
         */
        isNew() {
            return this.mode === MODE_NEW || this.isEditing;
        },
    },
};
</script>
<style scoped lang="scss">
    @use './CreditCardForm.scss';
</style>
